import { getFreshAccessToken, getRefreshToken, refreshAccessToken, expireSession, SessionExpiredError } from '../../../lib/api/api'
import { readSSEStream } from '../../../lib/sse'
import type { ChatContextSettings } from '../../../contexts/ChatContextProvider'
import { SOURCE_KEYS } from '../../../contexts/chatSources'
import type { SourceKey } from '../../../contexts/chatSources'
import type { AttachmentType } from '../../../contexts/AttachmentContext'

export interface ChatAttachment {
  id: string | number
  type: AttachmentType
}

export interface ChatHistoryItem {
  role: 'user' | 'assistant'
  content: string
}

/** JSON body of POST /chat-stream/stream (server ChatStreamRequest). The
 * server ignores unknown keys, so these names must match it exactly. */
export interface ChatStreamRequest {
  message: string
  // Searched sources (server src/agents/sources.py); empty = every source
  sources: SourceKey[]
  // Cantons of the cantonal-law source; absent = the firm's (server)
  cantons?: string[]
  attached_documents: Array<{ id: string | number; type: AttachmentType }>
  conversation_history: ChatHistoryItem[]
}

/** What the chat sends: the message, the selected sources, the attachments
 * and the previous turns (without the current message). */
export interface ChatRequestInput {
  message: string
  settings: ChatContextSettings
  attachments: ChatAttachment[]
  history: ChatHistoryItem[]
  cantons?: string[] | null
}

const toWireAttachment = ({ id, type }: ChatAttachment): ChatStreamRequest['attached_documents'][number] =>
  ({ id, type })

export function buildChatBody(
  message: string,
  settings: ChatContextSettings,
  attachments: ChatAttachment[],
  history: ChatHistoryItem[],
  cantons?: string[] | null,
): ChatStreamRequest {
  return {
    message,
    sources: SOURCE_KEYS.filter((key) => settings[key]),
    ...(cantons && cantons.length ? { cantons } : {}),
    attached_documents: attachments.map(toWireAttachment),
    conversation_history: history,
  }
}

/** Non-2xx HTTP response (other than an unrecoverable 401). */
export class ChatStreamHttpError extends Error {
  constructor(public readonly status: number, message: string) {
    super(message)
    this.name = 'ChatStreamHttpError'
  }
}

const STREAM_URL = '/api/v1/chat-stream/stream'

// Bearer token only: the server accepts no session cookie.
const postStream = (token: string, body: string, signal: AbortSignal) =>
  fetch(STREAM_URL, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      'Accept': 'text/event-stream',
    },
    body,
    signal,
  })

const throwIfAborted = (signal: AbortSignal) => {
  if (signal.aborted) throw new DOMException('Aborted', 'AbortError')
}

const readErrorDetail = async (resp: Response): Promise<string> => {
  try {
    const data = await resp.json()
    if (typeof data?.detail === 'string') return data.detail
    if (data?.detail) return JSON.stringify(data.detail)
  } catch { /* not JSON */ }
  return resp.statusText || `HTTP ${resp.status}`
}

/**
 * POST the chat request (body from buildChatBody) and stream the SSE
 * response, calling `onEvent` with every parsed JSON frame
 * (`{type: 'reasoning' | 'sources' | ...}`). Reading stops when `onEvent`
 * returns true (e.g. on 'done') or the stream ends.
 *
 * On 401 the access token is refreshed once (shared refresh) and the request
 * retried; if that fails the global session-expired flow is triggered and a
 * SessionExpiredError is thrown. Aborting `signal` rejects with an AbortError.
 */
export async function streamChat(
  request: ChatRequestInput,
  signal: AbortSignal,
  onEvent: (data: any) => boolean | void,
): Promise<void> {
  const body = JSON.stringify(buildChatBody(request.message, request.settings, request.attachments, request.history, request.cantons))
  const token = await getFreshAccessToken()
  if (!token) {
    // No token and no refresh token left: the session is over. Otherwise the
    // refresh failed for a transient reason (backend unreachable).
    if (!getRefreshToken()) throw new SessionExpiredError()
    throw new TypeError('Token refresh failed')
  }
  throwIfAborted(signal)

  let resp = await postStream(token, body, signal)

  if (resp.status === 401) {
    let retryToken: string
    try {
      retryToken = await refreshAccessToken()
    } catch (err) {
      if (err instanceof SessionExpiredError) throw err
      // Refresh failed for a transient reason (backend unreachable).
      throw new TypeError('Token refresh failed')
    }
    throwIfAborted(signal)
    resp = await postStream(retryToken, body, signal)
    if (resp.status === 401) {
      // A freshly refreshed token was still rejected: the session is gone.
      expireSession()
      throw new SessionExpiredError()
    }
  }

  if (!resp.ok) {
    throw new ChatStreamHttpError(resp.status, await readErrorDetail(resp))
  }
  if (!resp.body) {
    throw new TypeError('Streaming non supportato dal browser')
  }

  await readSSEStream(resp.body, (message) => {
    let data: any
    try {
      data = JSON.parse(message.data)
    } catch {
      return false // ignore malformed frames
    }
    return onEvent(data) === true
  })
}
